import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowUpRight } from "lucide-react";
import api from "@/lib/api";
import { optimizeImageUrl } from "@/lib/imageUtils";
import WhatsAppIcon from "@/components/WhatsAppIcon";

const WHATSAPP_NUMBER = process.env.REACT_APP_WHATSAPP_NUMBER || "917314344673";

export default function PromoBannerGrid() {
  const navigate = useNavigate();
  const [banners, setBanners] = useState([]);
  const [content, setContent] = useState({});

  useEffect(() => {
    api.get("/banners").then((res) => setBanners(res.data)).catch(() => {});
    api.get("/site-settings").then((res) => setContent(res.data.homepage_content?.promo_banners || {})).catch(() => {});
  }, []);

  if (banners.length === 0) return null;

  return (
    <section data-testid="promo-banner-section" className="py-16 sm:py-24 bg-[#FDFCFB]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-12">
          <div>
            <span data-testid="promo-banner-overline" className="text-xs tracking-[0.2em] uppercase font-bold text-[var(--brand-primary)]">{content.overline || "Explore"}</span>
            <h2 data-testid="promo-banner-title" className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight text-[#1A1A1A] mt-3">{content.title || "Popular Visa & Travel Categories"}</h2>
          </div>
          <p className="hidden sm:block text-sm text-[var(--brand-secondary)] max-w-xs">Tap any category to view details or chat with our team for an instant quote.</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5 sm:gap-6">
          {banners.map((b, i) => (
            <div key={b._id} data-testid={`promo-banner-${b._id}`}
              onClick={() => b.service_slug && navigate(`/services/${b.service_slug}`)}
              style={{ animationDelay: `${(i % 8) * 60}ms` }}
              className={`group relative rounded-2xl overflow-hidden h-44 sm:h-56 shadow-sm hover:shadow-2xl transition-all duration-500 hover:-translate-y-1.5 animate-fade-in-up ${b.service_slug ? "cursor-pointer" : ""}`}>
              <img src={optimizeImageUrl(b.image, 400)} alt={b.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-black/10 group-hover:from-black/90 transition-colors" />
              <ArrowUpRight size={16} className="absolute top-3 right-3 text-white/70 group-hover:text-white group-hover:rotate-45 transition-all duration-300" />
              <div className="relative h-full flex flex-col justify-end p-4">
                <h3 className="text-white font-heading text-base sm:text-lg leading-snug mb-3">{b.title}</h3>
                <a
                  href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(`Hi, I'm interested in ${b.title}`)}`}
                  target="_blank" rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  data-testid={`promo-banner-whatsapp-${b._id}`}
                >
                  <Button size="sm" className="bg-[#25D366] hover:bg-[#1ea952] text-white text-xs h-8 rounded-full shadow-md">
                    <WhatsAppIcon size={14} className="mr-1.5 text-white" /> WhatsApp
                  </Button>
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
